import apiClient from './apiClient';

export interface CustomFolder {
  id: string;
  name: string;
  description?: string;
  minio_path: string;
  organization_id: string;
  owner_id: string;
  /** Lot K: shared folders feed the org's knowledge; unshared ones only their owner's. */
  shared_with_org: boolean;
  /** Default admin folders: always shared, not deletable, admin-only uploads. */
  system_key?: 'compliance' | 'branding' | 'org_shared' | null;
  created_at: string;
  updated_at?: string;
}

export interface CustomFolderWithCount extends CustomFolder {
  document_count: number;
}

export interface CustomFolderCreate {
  name: string;
  description?: string;
}

export interface CustomFolderUpdate {
  name?: string;
  description?: string;
}

const apiBaseURL = '/api/custom-folders';
const apiCollectionURL = `${apiBaseURL}/`;

export const customFolderService = {
  // Create a new custom folder
  async create(folder: CustomFolderCreate): Promise<CustomFolder> {
    const response = await apiClient.post(apiCollectionURL, folder);
    return response.data;
  },

  // List all custom folders for the current organization
  async list(): Promise<CustomFolderWithCount[]> {
    const response = await apiClient.get(apiCollectionURL);
    return response.data;
  },

  // Get a specific custom folder by ID
  async get(folderId: string): Promise<CustomFolder> {
    const response = await apiClient.get(`${apiBaseURL}/${folderId}`);
    return response.data;
  },

  // Update a custom folder
  async update(folderId: string, folder: CustomFolderUpdate): Promise<CustomFolder> {
    const response = await apiClient.patch(`${apiBaseURL}/${folderId}`, folder);
    return response.data;
  },

  // Share the folder's knowledge with the organization, or keep it to its owner
  async setSharing(folderId: string, sharedWithOrg: boolean): Promise<CustomFolder> {
    const response = await apiClient.put(`${apiBaseURL}/${folderId}/sharing`, {
      shared_with_org: sharedWithOrg,
    });
    return response.data;
  },

  // Delete a custom folder
  async delete(folderId: string): Promise<void> {
    await apiClient.delete(`${apiBaseURL}/${folderId}`);
  },
};

export default customFolderService;
